import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import classnames from 'classnames';
import { isValid, set, startOfDay, endOfDay, isSameDay, isBefore, isEqual } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { translate } from '@micro-apps/env';
import Navigation from './Navigation';
import MonthView from './MonthView';
import { getBegin, getBeginNext, getEnd, getValueRange } from './shared/dates';
import { between } from './shared/utils';
import type {
	Action,
	ClassName,
	LooseValue,
	NavigationLabelFunc,
	OnArgs,
	OnClickFunc,
	OnClickWeekNumberFunc,
	Range,
	TileClassNameFunc,
	TileContentFunc,
	TileDisabledFunc,
	Value,
} from './shared/types';
import type {
	formatDay as defaultFormatDay,
	formatLongDate as defaultFormatLongDate,
	formatMonthYear as defaultFormatMonthYear,
	formatShortWeekday as defaultFormatShortWeekday,
	formatWeekday as defaultFormatWeekday,
} from './shared/dateFormatter';
import './Calendar.scss';

export type CalendarStatus = '0' | '9' | '.' | 'R' | '?';

const baseClassName = 'FSHCalendarView';

export type CalendarProps = {
	activeStartDate?: Date;
	allowPartialRange?: boolean;
	className?: ClassName;
	defaultActiveStartDate?: Date;
	defaultValue?: LooseValue;
	formatDay?: typeof defaultFormatDay;
	formatLongDate?: typeof defaultFormatLongDate;
	formatMonthYear?: typeof defaultFormatMonthYear;
	formatShortWeekday?: typeof defaultFormatShortWeekday;
	formatWeekday?: typeof defaultFormatWeekday;
	goToRangeStartOnSelect?: boolean;
	inputRef?: React.Ref<HTMLDivElement>;
	locale: string;
	maxDate?: Date;
	minDate?: Date;
	navigationAriaLabel?: string | ((activeStartDate: Date, showDoubleView?: boolean) => string);
	navigationAriaLive?: 'off' | 'polite' | 'assertive';
	navigationLabel?: NavigationLabelFunc;
	next2AriaLabel?: string;
	next2Label?: React.ReactNode;
	nextAriaLabel?: string;
	nextLabel?: React.ReactNode;
	onActiveStartDateChange?: ({ action, activeStartDate, value }: OnArgs) => void;
	onChange?: (value: Value, event: React.MouseEvent<HTMLButtonElement>) => void;
	onClickDay?: OnClickFunc;
	onClickWeekNumber?: OnClickWeekNumberFunc;
	prev2AriaLabel?: string;
	prev2Label?: React.ReactNode;
	prevAriaLabel?: string;
	prevLabel?: React.ReactNode;
	returnValue?: 'start' | 'end' | 'range';
	selectRange?: boolean;
	strictRangeSelect?: boolean;
	showDoubleView?: boolean;
	showFixedNumberOfWeeks?: boolean;
	showNavigation?: boolean;
	showNeighboringMonth?: boolean;
	showWeekNumbers?: boolean;
	tileClassName?: TileClassNameFunc | ClassName;
	tileContent?: TileContentFunc | React.ReactNode;
	tileDisabled?: TileDisabledFunc;
	value?: LooseValue;
	timezone?: string;
	tileAriaLabel?: (d: Date) => string;
};

function toDate(value: Date | string, timezone: string): Date {
	if (typeof value === 'string') {
		return toZonedTime(value, timezone);
	}

	return value;
}

function getValue(value: LooseValue | undefined, index: 0 | 1, timezone: string): Date | null {
	const rawValue = Array.isArray(value) ? value[index] : value;

	if (!rawValue) {
		return null;
	}

	const valueDate = toDate(rawValue, timezone);

	if (!isValid(valueDate)) {
		throw new Error(`Invalid date: ${value}`);
	}

	return valueDate;
}

type DetailArgs = {
	value?: LooseValue;
	minDate?: Date;
	maxDate?: Date;
	activeStartDate?: Date;
	timezone?: string;
	strictRangeSelect?: boolean;
};

function getDetailValue({ value, minDate, maxDate, timezone }: DetailArgs, index: 0 | 1) {
	const valuePiece = getValue(value, index, timezone!);

	if (!valuePiece) {
		return null;
	}

	const detailValueFrom = (() => {
		switch (index) {
			case 0:
				return getBegin(valuePiece);
			case 1:
				return getEnd(valuePiece);
			default:
				throw new Error(`Invalid index value: ${index}`);
		}
	})();

	return between(detailValueFrom, minDate, maxDate);
}

const getDetailValueFrom = (args: DetailArgs) => getDetailValue(args, 0);

const getDetailValueTo = (args: DetailArgs) => getDetailValue(args, 1);

const getDetailValueArray = (args: DetailArgs) => {
	const { value, strictRangeSelect } = args;
	if (strictRangeSelect && Array.isArray(value)) {
		const [start, end] = value;
		if (
			isSameDay(
				startOfDay(toZonedTime(end!, args.timezone!)),
				typeof start === 'string' ? new Date(start) : start!
			)
		) {
			args.value = [end, null];
		}
	}
	return [getDetailValueFrom, getDetailValueTo].map(fn => fn(args)) as [
		ReturnType<typeof getDetailValueFrom>,
		ReturnType<typeof getDetailValueTo>,
	];
};
function getActiveStartDate({ maxDate, minDate, value, timezone, activeStartDate }: DetailArgs) {
	const valueFrom =
		getDetailValueFrom({
			value,
			minDate,
			maxDate,
			timezone,
		}) || toZonedTime(new Date(), timezone!);

	if (activeStartDate && activeStartDate.getMonth() === valueFrom.getMonth() - 1) {
		return getBegin(activeStartDate);
	}

	return getBegin(valueFrom);
}

function getInitialActiveStartDate({
	activeStartDate,
	defaultActiveStartDate,
	defaultValue,
	maxDate,
	minDate,
	value,
	timezone,
}: {
	activeStartDate?: Date;
	defaultActiveStartDate?: Date;
	defaultValue?: LooseValue;
	maxDate: Date;
	minDate: Date;
	value?: LooseValue;
	timezone?: string;
}) {
	const valueFrom = activeStartDate || defaultActiveStartDate;

	if (valueFrom) {
		return getBegin(valueFrom);
	}

	return getActiveStartDate({
		activeStartDate,
		maxDate,
		minDate,
		value: value || defaultValue,
		timezone,
	});
}

function getIsSingleValue<T>(value: T | T[]): value is T {
	return value && (!Array.isArray(value) || value.length === 1);
}

function areDatesEqual(date1?: Date | null, date2?: Date | null) {
	return date1 instanceof Date && date2 instanceof Date && date1.getTime() === date2.getTime();
}

const Calendar = forwardRef(function Calendar(props: CalendarProps, ref) {
	const defaultMinDate = startOfDay(
		toZonedTime(set(new Date(), { year: 1, month: 0, date: 1 }), props.timezone!)
	);
	const defaultMaxDate = endOfDay(toZonedTime(new Date(8.64e15), props.timezone!));
	const {
		activeStartDate: activeStartDateProps,
		allowPartialRange,
		className,
		defaultActiveStartDate,
		defaultValue,
		formatDay,
		formatLongDate,
		formatMonthYear,
		formatShortWeekday,
		formatWeekday,
		goToRangeStartOnSelect = true,
		inputRef,
		locale,
		maxDate = defaultMaxDate,
		minDate = defaultMinDate,
		navigationAriaLabel,
		navigationAriaLive,
		navigationLabel,
		next2AriaLabel,
		next2Label,
		nextAriaLabel,
		nextLabel,
		onActiveStartDateChange,
		onChange: onChangeProps,
		onClickDay,
		onClickWeekNumber,
		prev2AriaLabel,
		prev2Label,
		prevAriaLabel,
		prevLabel,
		returnValue = 'start',
		selectRange,
		showDoubleView,
		showFixedNumberOfWeeks,
		showNavigation = true,
		showNeighboringMonth = true,
		showWeekNumbers,
		tileAriaLabel,
		tileClassName,
		tileContent,
		tileDisabled,
		value: valueProps,
		timezone,
		strictRangeSelect,
	} = props;

	const [activeStartDateState, setActiveStartDateState] = useState<Date | null | undefined>(
		defaultActiveStartDate
	);
	const [hoverState, setHoverState] = useState<Date | null>(null);
	const [valueState, setValueState] = useState<Value | undefined>(
		Array.isArray(defaultValue)
			? (defaultValue.map(el => (el !== null ? toDate(el, timezone!) : null)) as Range<Date | null>)
			: defaultValue !== null && defaultValue !== undefined
				? toDate(defaultValue, timezone!)
				: null
	);

	const activeStartDate =
		activeStartDateProps ||
		activeStartDateState ||
		getInitialActiveStartDate({
			activeStartDate: activeStartDateProps,
			defaultActiveStartDate,
			defaultValue,
			maxDate,
			minDate,
			value: valueProps,
			timezone,
		});

	const value: Value = (() => {
		const rawValue = (() => {
			// In the middle of range selection, use value from state
			if (selectRange && getIsSingleValue(valueState)) {
				return valueState;
			}

			return valueProps !== undefined ? valueProps : valueState;
		})();

		if (!rawValue) {
			return null;
		}

		return Array.isArray(rawValue)
			? (rawValue.map(el => (el !== null ? toDate(el, timezone!) : null)) as Range<Date | null>)
			: rawValue !== null
				? toDate(rawValue, timezone!)
				: null;
	})();

	const hover = selectRange ? hoverState : null;

	const getProcessedValue = useCallback(
		(value: Date) => {
			const processFunction = (() => {
				switch (returnValue) {
					case 'start':
						return getDetailValueFrom;
					case 'end':
						return getDetailValueTo;
					case 'range':
						return getDetailValueArray;
					default:
						throw new Error('Invalid returnValue.');
				}
			})();

			return processFunction({
				maxDate,
				minDate,
				value,
				timezone,
			});
		},
		[maxDate, minDate, returnValue, timezone]
	);

	const setActiveStartDate = useCallback(
		(nextActiveStartDate: Date, action: Action) => {
			setActiveStartDateState(nextActiveStartDate);

			const args: OnArgs = {
				action,
				activeStartDate: nextActiveStartDate,
				value,
			};

			if (onActiveStartDateChange && !areDatesEqual(activeStartDate, nextActiveStartDate)) {
				onActiveStartDateChange(args);
			}
		},
		[activeStartDate, onActiveStartDateChange, value]
	);

	const onClickTile = useCallback(
		(value: Date, event: React.MouseEvent<HTMLButtonElement>) => {
			const callback = (() => {
				return onClickDay;
			})();

			if (callback) callback(value, event);
		},
		[onClickDay]
	);

	const onChange = useCallback(
		(rawNextValue: Date, event: React.MouseEvent<HTMLButtonElement>) => {
			const previousValue = value;

			onClickTile(rawNextValue, event);

			const isFirstValueInRange = selectRange && !getIsSingleValue(previousValue);

			let nextValue: Value;
			if (selectRange) {
				// Range selection turned on

				if (isFirstValueInRange) {
					// Value has 0 or 2 elements - either way we're starting a new array
					// First value
					nextValue = getBegin(rawNextValue);
				} else {
					if (!previousValue) {
						throw new Error('previousValue is required');
					}

					if (Array.isArray(previousValue)) {
						throw new Error('previousValue must not be an array');
					}

					const nextValueZoned = toZonedTime(rawNextValue, timezone!);
					// In strict range select, selecting a second date in the past results in a new first date.
					if (
						(strictRangeSelect && isEqual(nextValueZoned, previousValue)) ||
						isBefore(nextValueZoned, previousValue)
					) {
						nextValue = getBegin(rawNextValue);
					} else {
						// Second value
						nextValue = getValueRange(previousValue, rawNextValue);
					}
				}
			} else {
				// Range selection turned off
				nextValue = getProcessedValue(rawNextValue);
			}

			const nextActiveStartDate =
				// Range selection turned off
				!selectRange ||
				// Range selection turned on, first value
				isFirstValueInRange ||
				// Range selection turned on, second value, goToRangeStartOnSelect toggled on
				goToRangeStartOnSelect ||
				// Range select reseulted in a reset
				(strictRangeSelect && !Array.isArray(nextValue))
					? getActiveStartDate({
							activeStartDate,
							maxDate,
							minDate,
							value: nextValue,
							timezone,
						})
					: null;

			event.persist();

			if (
				showDoubleView &&
				nextActiveStartDate &&
				activeStartDate &&
				activeStartDate.getMonth() !== nextActiveStartDate?.getMonth() - 1
			) {
				setActiveStartDateState(nextActiveStartDate);
			}
			setValueState(nextValue);

			const args: OnArgs = {
				action: 'onChange',
				activeStartDate: nextActiveStartDate,
				value: nextValue,
			};

			if (onActiveStartDateChange && !areDatesEqual(activeStartDate, nextActiveStartDate)) {
				onActiveStartDateChange(args);
			}

			if (onChangeProps) {
				if (selectRange) {
					const isSingleValue = getIsSingleValue(nextValue);

					if (!isSingleValue) {
						onChangeProps(nextValue || null, event);
					} else if (allowPartialRange) {
						if (Array.isArray(nextValue)) {
							throw new Error('value must not be an array');
						}

						onChangeProps([nextValue || null, null], event);
					}
				} else {
					onChangeProps(nextValue || null, event);
				}
			}
		},
		[
			activeStartDate,
			allowPartialRange,
			getProcessedValue,
			goToRangeStartOnSelect,
			maxDate,
			minDate,
			onActiveStartDateChange,
			onChangeProps,
			onClickTile,
			selectRange,
			value,
			timezone,
			showDoubleView,
			strictRangeSelect,
		]
	);

	function onMouseOver(nextHover: Date) {
		setHoverState(nextHover);
	}

	function onMouseLeave() {
		setHoverState(null);
	}

	useImperativeHandle(
		ref,
		() => ({
			activeStartDate,
			onChange,
			setActiveStartDate,
			value,
		}),
		[activeStartDate, onChange, setActiveStartDate, value]
	);

	function renderContent(next?: boolean) {
		const currentActiveStartDate = next ? getBeginNext(activeStartDate) : getBegin(activeStartDate);

		const onClick = onChange;

		const commonProps = {
			activeStartDate: currentActiveStartDate,
			hover,
			locale,
			maxDate,
			minDate,
			onClick,
			onMouseOver: selectRange ? onMouseOver : undefined,
			tileAriaLabel,
			tileClassName,
			tileContent,
			tileDisabled,
			value,
		};

		return (
			<MonthView
				formatDay={formatDay}
				formatLongDate={formatLongDate}
				formatShortWeekday={formatShortWeekday}
				formatWeekday={formatWeekday}
				onClickWeekNumber={onClickWeekNumber}
				onMouseLeave={selectRange ? onMouseLeave : undefined}
				showFixedNumberOfWeeks={
					typeof showFixedNumberOfWeeks !== 'undefined' ? showFixedNumberOfWeeks : showDoubleView
				}
				showNeighboringMonth={showNeighboringMonth}
				showWeekNumbers={showWeekNumbers}
				{...commonProps}
			/>
		);
	}

	function renderNavigation() {
		if (!showNavigation) {
			return null;
		}

		return (
			<Navigation
				activeStartDate={activeStartDate}
				formatMonthYear={formatMonthYear}
				locale={locale}
				maxDate={maxDate}
				minDate={minDate}
				navigationAriaLabel={navigationAriaLabel}
				navigationAriaLive={navigationAriaLive}
				navigationLabel={navigationLabel}
				next2AriaLabel={next2AriaLabel}
				next2Label={next2Label}
				nextAriaLabel={nextAriaLabel}
				nextLabel={nextLabel}
				prev2AriaLabel={prev2AriaLabel}
				prev2Label={prev2Label}
				prevAriaLabel={prevAriaLabel}
				prevLabel={prevLabel}
				setActiveStartDate={setActiveStartDate}
				showDoubleView={showDoubleView}
			/>
		);
	}

	const valueArray = Array.isArray(value) ? value : [value];

	return (
		<div
			className={classnames(
				baseClassName,
				selectRange && valueArray.length === 1 && `${baseClassName}--selectRange`,
				showDoubleView && `${baseClassName}--doubleView`,
				className
			)}
			ref={inputRef}
			aria-label={translate('Stay Dates')}
		>
			{renderNavigation()}
			<div
				className={`${baseClassName}__viewContainer`}
				onBlur={selectRange ? onMouseLeave : undefined}
				onMouseLeave={selectRange ? onMouseLeave : undefined}
			>
				{renderContent()}
				{showDoubleView ? renderContent(true) : null}
			</div>
		</div>
	);
});

export default Calendar;
